import * as React from 'react';
import { useState } from 'react';

import { Typography2, VisuallyHidden } from '@coursera/cds-core';

import type { ResolutionOption } from 'bundles/video-player/types/VideoPlayer';

import _t from 'i18n!nls/general-video-player';

import 'css!./__styles__/ResolutionChangeSection';

type Props = {
  currentResolution: ResolutionOption;
  supportedResolutions: ResolutionOption[];
  onIncreaseResolutionClick: (event: React.MouseEvent<HTMLElement>) => void;
  onDecreaseResolutionClick: (event: React.MouseEvent<HTMLElement>) => void;
};

const getAriaLabelResolutions = (
  currentResolution: ResolutionOption,
  supportedResolutions: ResolutionOption[]
): {
  lowerResolution?: ResolutionOption;
  higherResolution?: ResolutionOption;
} => {
  const currentIndex = supportedResolutions.indexOf(currentResolution);
  const higherResolution = supportedResolutions[currentIndex + 1];
  const lowerResolution = supportedResolutions[currentIndex - 1];

  return {
    lowerResolution,
    higherResolution,
  };
};

const ResolutionChangeSection = ({
  currentResolution,
  supportedResolutions,
  onDecreaseResolutionClick,
  onIncreaseResolutionClick,
}: Props) => {
  const resolutionMap: Partial<Record<ResolutionOption, string>> = {
    '240p': _t('Lowest'),
    '360p': _t('Low'),
    '540p': _t('Medium'),
    '720p': _t('High'),
    '1080p': _t('Highest'),
  };

  const [wasAButtonClicked, setWasAButtonClicked] = useState<boolean>(false);

  const resolutionDisplayText = `${currentResolution} (${resolutionMap[currentResolution]})`;

  const isDecreaseDisabled = currentResolution === supportedResolutions[0];
  const isIncreaseDisabled = currentResolution === supportedResolutions[supportedResolutions.length - 1];
  const { lowerResolution, higherResolution } = getAriaLabelResolutions(currentResolution, supportedResolutions);

  return (
    <div className="rc-ResolutionChangeSection">
      <Typography2
        component="div"
        variant="subtitleMedium"
        className="resolution-change-header horizontal-box align-items-absolute-center"
        color="invertBody"
      >
        {_t('Video Quality')}
      </Typography2>
      <div className="video-setting-separator" />
      <div className="resolution-change-controls horizontal-box align-items-absolute-center">
        <button
          type="button"
          aria-label={
            lowerResolution
              ? _t('decrease video quality to #{lowerResolution}', { lowerResolution })
              : _t('decrease video quality')
          }
          onClick={(event) => {
            setWasAButtonClicked(true);
            onDecreaseResolutionClick(event);
          }}
          disabled={isDecreaseDisabled}
        >
          <span className="cif-minus" />
        </button>

        {/* After learner clicks a button, render this additional status element to announce the update to screen readers. */}
        {wasAButtonClicked && (
          <VisuallyHidden role="status" aria-live="polite" component="span" className="rc-A11yScreenReaderOnly">
            {resolutionDisplayText}
          </VisuallyHidden>
        )}

        {/* Before the learner clicks a button, expose the visible resolution label to screen readers.
            Rendering it after a button is clicked would result in duplicate labels exposed to screen readers, which is unwanted. */}
        <Typography2
          variant="bodySecondary"
          color="invertBody"
          component="span"
          aria-hidden={wasAButtonClicked}
          className="resolution-text"
        >
          {resolutionDisplayText}
        </Typography2>

        <button
          type="button"
          aria-label={
            higherResolution
              ? _t('increase video quality to #{higherResolution}', { higherResolution })
              : _t('increase video quality')
          }
          onClick={(event) => {
            setWasAButtonClicked(true);
            onIncreaseResolutionClick(event);
          }}
          disabled={isIncreaseDisabled}
        >
          <span className="cif-plus" />
        </button>
      </div>
    </div>
  );
};

export default ResolutionChangeSection;
